import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, TrendingDown, TrendingUp, Minus, MoveVertical, CalendarClock, RefreshCw, ArrowRight } from "lucide-react";
import { PortalLayout } from "@/components/portal/PortalLayout";
import { portalApi } from "@/lib/portalApi";
import { TRAIT_LABELS } from "@/data/questionnaires";

const DIRECTION = {
  better: { color: "#4ADE80", icon: TrendingDown, word: "improving" },
  worse: { color: "#F87171", icon: TrendingUp, word: "worsening" },
  shift: { color: "#D4AF37", icon: MoveVertical, word: "shifted" },
  flat: { color: "#94A3B8", icon: Minus, word: "unchanged" },
};

const DeltaRow = ({ item, testid }) => {
  const meta = DIRECTION[item.direction];
  const Icon = meta.icon;
  const sign = item.change > 0 ? "+" : "";
  return (
    <div className="flex items-center gap-4 py-3 border-b border-slate-800/70 last:border-0" data-testid={testid}>
      <span className="text-slate-300 text-sm flex-1">{TRAIT_LABELS[item.trait] || item.trait}</span>
      <span className="font-mono text-sm text-white w-12 text-right">{item.current}%</span>
      {item.previous === null || item.previous === undefined ? (
        <span className="text-slate-600 text-xs w-28 text-right">first measurement</span>
      ) : (
        <span className="flex items-center justify-end gap-1.5 w-28 text-xs font-mono" style={{ color: meta.color }}>
          <Icon size={13} />
          {sign}
          {item.change} pts
        </span>
      )}
    </div>
  );
};

export default function ProgressPage() {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    portalApi
      .progress()
      .then(setProgress)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <PortalLayout>
        <div className="flex justify-center py-24" data-testid="progress-loading">
          <Loader2 className="animate-spin text-[#D4AF37]" size={26} />
        </div>
      </PortalLayout>
    );
  }

  const inc = progress.incidents;
  const incMeta = DIRECTION[inc.direction];

  return (
    <PortalLayout>
      <div data-testid="progress-page">
        <div className="mb-10 max-w-3xl">
          <p className="text-[#D4AF37] text-xs uppercase tracking-[0.28em] mb-3">Check-in</p>
          <h1 className="font-serif text-3xl md:text-4xl text-white mb-4">Is it actually getting better?</h1>
          <p className="text-slate-400 text-sm leading-relaxed" data-testid="progress-headline">
            {progress.headline}
          </p>
        </div>

        <div
          className={`border p-6 md:p-8 mb-10 flex flex-col md:flex-row md:items-center justify-between gap-5 ${
            progress.reassessment_due ? "border-[#D4AF37]/40 bg-[#D4AF37]/[0.06]" : "border-slate-800 bg-[#0B1220]/70"
          }`}
          data-testid="progress-due-banner"
        >
          <div className="flex items-start gap-4">
            <CalendarClock className="text-[#D4AF37] shrink-0 mt-0.5" size={18} />
            <div>
              <p className="text-white text-sm mb-1">
                {progress.reassessment_due
                  ? "Your quarterly check-in is due"
                  : `Next check-in in ${progress.next_due_in_days} days`}
              </p>
              <p className="text-slate-500 text-sm">
                Last measured {progress.days_since_assessment} days ago · {progress.check_ins_completed} check-in
                {progress.check_ins_completed === 1 ? "" : "s"} completed
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 shrink-0">
            <Link
              to="/portal/big-five"
              data-testid="progress-retake-big-five"
              className="inline-flex items-center gap-2 bg-[#D4AF37] text-slate-950 px-6 py-3 text-[11px] uppercase tracking-[0.2em] font-medium hover:bg-[#B5952F] transition-colors"
            >
              <RefreshCw size={13} /> Re-take Big Five
            </Link>
            <Link
              to="/portal/incident/new"
              data-testid="progress-log-incident"
              className="inline-flex items-center gap-2 border border-slate-700 text-slate-300 px-6 py-3 text-[11px] uppercase tracking-[0.2em] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
            >
              Log what's happening now
            </Link>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-5 mb-10">
          <div className="border border-slate-800 bg-[#0B1220]/70 p-6 md:p-8" data-testid="progress-big-five">
            <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-5">You, then vs now</p>
            {progress.big_five.length ? (
              progress.big_five.map((item) => (
                <DeltaRow key={item.trait} item={item} testid={`progress-bf-${item.trait}`} />
              ))
            ) : (
              <p className="text-slate-500 text-sm">
                No Big Five results yet.{" "}
                <Link to="/portal/big-five" className="text-[#D4AF37] hover:underline">
                  Take it now
                </Link>
              </p>
            )}
          </div>

          <div className="border border-slate-800 bg-[#0B1220]/70 p-6 md:p-8" data-testid="progress-dark-triad">
            <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-5">Your leader, then vs now</p>
            {progress.dark_triad.length ? (
              progress.dark_triad.map((item) => (
                <DeltaRow key={item.trait} item={item} testid={`progress-dt-${item.trait}`} />
              ))
            ) : (
              <p className="text-slate-500 text-sm">
                No Leader D3 results yet — run one from any incident to start tracking.
              </p>
            )}
          </div>
        </div>

        <div className="border border-slate-800 bg-[#0B1220]/70 p-6 md:p-8 mb-10" data-testid="progress-incidents">
          <p className="text-[11px] uppercase tracking-[0.24em] text-slate-500 mb-6">Incident frequency</p>
          <div className="flex flex-wrap items-end gap-10">
            <div>
              <p className="font-serif text-4xl text-white leading-none">{inc.last_90_days}</p>
              <p className="text-[11px] uppercase tracking-widest text-slate-500 mt-2">Last 90 days</p>
            </div>
            <div>
              <p className="font-serif text-4xl text-slate-500 leading-none">{inc.before_that}</p>
              <p className="text-[11px] uppercase tracking-widest text-slate-500 mt-2">Before that</p>
            </div>
            {inc.has_baseline ? (
              <span className="flex items-center gap-2 text-xs uppercase tracking-widest" style={{ color: incMeta.color }} data-testid="progress-incident-direction">
                <incMeta.icon size={14} /> {incMeta.word}
              </span>
            ) : (
              <span className="text-xs uppercase tracking-widest text-slate-600" data-testid="progress-incident-baseline">
                No earlier period to compare yet
              </span>
            )}
          </div>
        </div>

        <div className="border border-slate-800 bg-[#0B1220]/70 p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-5">
          <div>
            <h3 className="font-serif text-lg text-white mb-2">Nothing moving?</h3>
            <p className="text-slate-400 text-sm max-w-xl leading-relaxed">
              Flat numbers over a quarter usually mean the tactics need changing, not more patience. Bring this page to a
              session with a clinic behavioural scientist.
            </p>
          </div>
          <Link
            to="/"
            data-testid="progress-book-session"
            className="shrink-0 inline-flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] px-6 py-3 text-[11px] uppercase tracking-[0.2em] hover:bg-[#D4AF37] hover:text-slate-950 transition-colors"
          >
            Book a session <ArrowRight size={13} />
          </Link>
        </div>
      </div>
    </PortalLayout>
  );
}
